import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react';
import './InfoTip.css';

interface InfoTipProps {
  /** Tooltip body. Keep it to a sentence or two — this is read mid-task. */
  content: ReactNode;
  /** Optional trigger; defaults to a small info glyph. */
  children?: ReactNode;
  label?: string;
}

/**
 * Hover/focus tooltip with a real popup rather than the native `title` attribute,
 * which has a ~1s delay and can't be keyboard-reached.
 *
 * The bubble is positioned `fixed` off the trigger's measured rect: these render
 * inside scrolling panels, and an absolutely-positioned bubble is clipped by the
 * scroll container so it never appears at all.
 */
export function InfoTip({ content, children, label = 'More information' }: InfoTipProps) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ top: r.top, left: r.left + r.width / 2 });
  }, []);

  const close = useCallback(() => setPos(null), []);

  useEffect(() => {
    if (!pos) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    // A fixed bubble would otherwise detach from its trigger as the panel scrolls.
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [pos, close]);

  const toggle = () => { if (pos) close(); else place(); };
  // On touch the tap's focus would open the tip and its click would immediately close it again.
  const pointerRef = useRef<string | null>(null);

  return (
    <span className="infotip">
      <span
        ref={triggerRef}
        className="infotip-trigger"
        role="button"
        tabIndex={0}
        aria-label={label}
        onPointerDown={(e) => { pointerRef.current = e.pointerType; }}
        onPointerEnter={(e) => { if (e.pointerType === 'mouse') place(); }}
        onPointerLeave={(e) => { if (e.pointerType === 'mouse') close(); }}
        onFocus={() => { if (!pointerRef.current || pointerRef.current === 'mouse') place(); }}
        onBlur={close}
        onClick={(e) => { e.stopPropagation(); toggle(); pointerRef.current = null; }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
        }}
      >
        {children ?? <span className="infotip-glyph" aria-hidden="true">i</span>}
      </span>
      {pos && (
        <span
          className="infotip-bubble"
          role="tooltip"
          style={{ top: pos.top, left: pos.left }}
        >
          {content}
        </span>
      )}
    </span>
  );
}

export default InfoTip;
